import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Card, Center, Stack, Text, Title, Alert, TextInput, PasswordInput } from '@mantine/core';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { Roles } from '@bmai/shared';
import { useAuth } from '@/features/auth/AuthContext';

/**
 * Operator sign-in — a branded email + password form posted to the BFF
 * (`/api/auth/operator/login`), which runs a server-side password grant. No
 * Keycloak redirect. Already-authenticated operators skip straight to the console.
 */
export default function OperatorLoginPage(): React.ReactElement {
    const { isLoading, isAuthenticated, hasRole } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    if (!isLoading && isAuthenticated && hasRole(Roles.PLATFORM_OPERATOR)) {
        return <Navigate to="/operator/tenants" replace />;
    }

    const submit = async (): Promise<void> => {
        setBusy(true);
        setError(null);
        try {
            const res = await fetch('/_auth/api/auth/operator/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
            });
            if (res.ok) {
                window.location.assign('/operator/tenants');
                return;
            }
            setError('Invalid email or password.');
            setBusy(false);
        } catch {
            setError('Something went wrong. Please try again.');
            setBusy(false);
        }
    };

    return (
        <Center h="100vh" p="md">
            <Card withBorder shadow="sm" padding="xl" radius="md" w={400}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        void submit();
                    }}
                >
                    <Stack align="center" gap="md">
                        <ShieldCheck size={40} />
                        <Title order={3}>Operator Console</Title>
                        <Text c="dimmed" ta="center" size="sm">
                            Sign in with your platform-operator account.
                        </Text>
                        {error && (
                            <Alert color="red" icon={<AlertTriangle size={16} />} w="100%">
                                {error}
                            </Alert>
                        )}
                        <TextInput
                            w="100%"
                            label="Email"
                            type="email"
                            autoComplete="username"
                            value={email}
                            onChange={(e) => setEmail(e.currentTarget.value)}
                            autoFocus
                        />
                        <PasswordInput
                            w="100%"
                            label="Password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.currentTarget.value)}
                        />
                        <Button type="submit" fullWidth loading={busy}>
                            Sign in
                        </Button>
                    </Stack>
                </form>
            </Card>
        </Center>
    );
}
